import { ColDef, RowDoubleClickedEvent } from '@ag-grid-community/core';
import { Box, Flex, Icon, useBreakpointValue, VStack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionMenu } from '../../../components/action-menu';
import { AddressDto, PharmacyDto } from '../../../lib/ServerApi';
import { api } from '../../../lib/api';
import { LinkButton } from '@/components/ui/link-button';
import { Plus, SquarePen } from 'lucide-react';
import { Grid } from '@/components/ui/grid';
import { Address } from '@/components/ui/address';

const defaultColumnDef: ColDef<PharmacyDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
};

export function PharmaciesPage() {
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const query = useQuery<PharmacyDto[]>({
        queryKey: ['pharmacy', 'list'],
        queryFn: async () => {
            const { data } = await api.pharmacy.list();
            return data;
        },
    });

    const colDefs = useMemo<ColDef<PharmacyDto>[]>(() => {
        if (isMobile) {
            return [
                {
                    field: 'name',
                    headerName: 'Pharmacy',
                    pinned: 'left',
                    suppressAutoSize: true,
                    initialWidth: 200,
                    cellRenderer: ({ data }: { data: PharmacyDto }) => {
                        const addressText = data.address
                            ? `${data.address.city ? data.address.city : ''}${data.address.state ? `, ${data.address.state}` : ''}`.trim()
                            : 'No address';

                        return (
                            <VStack align="start" py={2} h="100%">
                                <Text fontWeight="medium" fontSize="sm" lineHeight="short">
                                    {data.name || 'No name'}
                                </Text>
                                <Text fontSize="xs" color="gray.600" lineHeight="short">
                                    {addressText}
                                </Text>
                            </VStack>
                        );
                    },
                },
                {
                    field: 'phone',
                    headerName: 'Phone',
                    width: 120,
                },
                {
                    headerName: '',
                    width: 50,
                    pinned: 'right',
                    cellRenderer: ({ data }: { data: PharmacyDto }) => {
                        return (
                            <Box onClick={(e) => e.stopPropagation()}>
                                <Flex justifyContent="center" alignItems="center" h="100%">
                                    <ActionMenu
                                        actions={[
                                            {
                                                id: 'primary',
                                                action: () => navigate(`/settings/pharmacies/${data.id}/edit`),
                                                icon: (
                                                    <Icon boxSize={4}>
                                                        <SquarePen />
                                                    </Icon>
                                                ),
                                                label: 'Edit',
                                            },
                                        ]}
                                    />
                                </Flex>
                            </Box>
                        );
                    },
                },
            ];
        }

        return [
            {
                field: 'name',
                headerName: 'Name',
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: true,
                resizable: false,
            },
            {
                field: 'address',
                headerName: 'Address',
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: true,
                resizable: false,
                cellRenderer: ({ value }: { value: AddressDto | null }) => {
                    return value ? <Address address={value} /> : '';
                },
            },
            {
                field: 'phone',
                headerName: 'Phone',
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: true,
                resizable: false,
            },
            {
                headerName: 'Actions',
                suppressHeaderFilterButton: true,
                resizable: false,
                cellRenderer: ({ data }: { data: PharmacyDto }) => {
                    return (
                        <Box onClick={(e) => e.stopPropagation()}>
                            <ActionMenu
                                actions={[
                                    {
                                        id: 'primary',
                                        action: () => navigate(`/settings/pharmacies/${data.id}/edit`),
                                        icon: (
                                            <Icon boxSize={5}>
                                                <SquarePen />
                                            </Icon>
                                        ),
                                        label: 'Edit',
                                    },
                                ]}
                            />
                        </Box>
                    );
                },
            },
        ];
    }, [isMobile, navigate]);

    const handleRowDoubleClick = (event: RowDoubleClickedEvent<PharmacyDto>) => {
        navigate(`/settings/pharmacies/${event.data?.id}/edit`);
    };

    return (
        <Flex px={{ base: '16px', md: 0 }} pt="16px" h="100%" flexDirection="column">
            <Flex alignItems="center" justifyContent="space-between" pb="12px" px={{ base: 0, md: '16px' }} gap="8px">
                <div />
                <Flex>
                    <LinkButton size="xs" to="/settings/pharmacy/create" textDecoration="none">
                        <Plus /> Add Pharmacy
                    </LinkButton>
                </Flex>
            </Flex>
            <Box position="relative" w="100%" h="100%" pb="24px">
                <Grid
                    key={isMobile ? 'mobile-grid' : 'grid'}
                    rowSelection="single"
                    columnDefs={colDefs}
                    rowData={query.data}
                    suppressCellFocus
                    defaultColDef={defaultColumnDef}
                    autoSizeStrategy={isMobile ? undefined : { type: 'fitGridWidth' }}
                    getRowHeight={isMobile ? () => 60 : undefined}
                    onRowDoubleClicked={handleRowDoubleClick}
                />
            </Box>
        </Flex>
    );
}
